import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  AppBar,
  Toolbar,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Zoom,
  Slide,
  Link as MuiLink
} from '@mui/material';
import {
  Menu as MenuIcon,
  DesignServices,
  Code,
  PhoneAndroid,
  Cloud,
  Security,
  Analytics
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { Link as RouterLink } from 'react-router-dom';
import Footer from '../../components/footer/Footer';
import taskOrbit from '../../images/taskOrbit.png';
import CRMOrbit from '../../images/CRMOrbit.png';
import UFSNetworks from '../../logo.svg';

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
    },
    secondary: {
      main: '#ec4899',
      light: '#f472b6',
      dark: '#db2777',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    h2: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

// Floating animation
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// Service data
const services = [
  {
    icon: <img src={taskOrbit} />,
    title: 'TASK Orbit',
    description: (
    <ul>
      <li style={{marginBottom:"8px"}}>Streamlines SDLC with task, issue & scrum management</li>
      <li style={{marginBottom:"8px"}}>Real-time project tracking and collaboration</li>
      <li>Ensures on-time delivery and improved productivity</li>
    </ul>
  ),
    color: '#6366f1',
    link: 'https://taskorbit-client.vercel.app/'
  },
  {
    icon: <img src={CRMOrbit} />,
    title: 'CRM Orbit',
    description: (
    <ul>
      <li style={{marginBottom:"8px"}}>Manage leads, deals, and customer interactions</li>
      <li style={{marginBottom:"8px"}}>AI-driven campaign automation and lead gen</li>
      <li>SEO & marketing analytics for higher ROI</li>
    </ul>
  ),
    color: '#ec4899',
    link: 'https://client-1-rosy.vercel.app/'
  },
  {
    icon: <img src={UFSNetworks} />,
    title: 'UFS Networks',
    description: (
    <ul>
      <li style={{marginBottom:"8px"}}>Monitor system performance<br/> and usage</li>
      <li style={{marginBottom:"8px"}}>Manage user roles and permissions</li>
      <li>Configure services and security <br/>settings</li>
    </ul>
  ),
    color: '#10b981',
    link: 'https://www.ufsnetworks.com/login'
  },
];

// Main component
function CustomerExperience() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [visibleServices, setVisibleServices] = useState([]);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Animation effect for services
  useEffect(() => {
    const timer = setTimeout(() => {
      services.forEach((_, index) => {
        setTimeout(() => {
          setVisibleServices(prev => [...prev, index]);
        }, index * 200);
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Drawer component
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        Company Name
      </Typography>
      <List>
        {['Home', 'Services', 'Portfolio', 'About', 'Contact'].map((item) => (
          <ListItem key={item} button>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Navigation Bar */}
        {/* <AppBar position="static" sx={{ backgroundColor: 'background.paper', color: 'text.primary', boxShadow: 'none' }}>
          <Container maxWidth="lg">
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
                Company<span style={{ color: theme.palette.primary.main }}>Name</span>
              </Typography>
              {isMobile ? (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <Box sx={{ display: 'flex', gap: 3 }}>
                  {['Home', 'Services', 'Portfolio', 'About', 'Contact'].map((item) => (
                    <Button key={item} color="inherit">
                      {item}
                    </Button>
                  ))}
                  <Button variant="contained" sx={{ borderRadius: 2 }}>
                    Get Started
                  </Button>
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar> */}

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
        >
          {drawer}
        </Drawer>

        {/* Hero Section */}
        <Box sx={{ 
          bgcolor: 'background.default', 
          py: 10,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`
        }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
              <Fade in={true} timeout={1000}>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  gutterBottom 
                  sx={{ 
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2
                  }}
                >
                  Unified Access to Our Product Platforms
                </Typography>
              </Fade>
              <Slide in={true} direction="up" timeout={800}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                  Seamlessly manage and navigate across all UFS Networks solutions through a single, integrated admin panel designed for efficiency, visibility, and control.
                </Typography>
              </Slide>
            </Box>
          </Container>
        </Box>

        {/* Services Section */}
        <Container maxWidth="lg" sx={{ py: 8, flex: 1 }}>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in={visibleServices.includes(index)} timeout={800}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 12px 30px -10px ${alpha(service.color, 0.3)}`,
                       // animation: `${float} 2s ease-in-out 1`,
                      },
                    }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: 120,
                        background: `linear-gradient(135deg, ${alpha(service.color, 0.1)} 0%, ${alpha(service.color, 0.05)} 100%)`,
                        color: service.color,
                        padding:'8px 16px',
                      }}
                    >
                      {service.icon}
                    </Box>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h5" component="h2" fontWeight="600">
                        {service.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {service.description}
                      </Typography>
                    </CardContent>
                    <Box sx={{ p: 2 }}>
                      <MuiLink component={RouterLink} to={service.link} underline='none' sx={{width: "100%", background: "#f2f2fe", padding: "16px", borderRadius:"32px", textAlign:"center",  color: "#0a4066", border:"solid 1px #cdcdff", transition: "all 0.3s ease",'&:hover': {backgroundColor: "#0a4066", color:'#fff', scale:(1.05)},}}>
                        Login
                      </MuiLink>
                    </Box>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      <Footer/>
    </ThemeProvider>
  );
}

export default CustomerExperience;